class RebuyProductRenderer {
  constructor(widgetId) {
    this.widgetId = widgetId;
    this.tabs = document.querySelectorAll('.rebuy-bundle-builder__step-tab');
    this.widget = window.Rebuy?.widgets?.find(w => String(w.id) === String(widgetId)) || null;
    this.steps = this.widget?.data?.settings?.steps || [];
    this.products = this.widget?.data?.products || [];
    this.storefrontToken = '2de55fbb1d39c87ab5a4c39cc56244c7';
    this.apiVersion = '2023-04';

    // internal
    this._currencyMo = null;
    this._currencyRunning = false;
    this._installedAddCheckout = false;
  }

  // --------- UTIL / STATE ---------
  refreshWidgetState() {
    this.widget = window.Rebuy?.widgets?.find(w => String(w.id) === String(this.widgetId)) || null;
    this.steps  = this.widget?.data?.settings?.steps || [];
    this.products = this.widget?.data?.products || [];
  }

  replaceDiscountPercentWithText() {
    if (!window.Shopify || !Shopify.currency) return;
  
    const activeCurrency = Shopify.currency.active || 'USD';
    const currencyRate = parseFloat(Shopify.currency.rate) || 1;
    const currencySymbol = Shopify.currency.symbol || '$';
  
    const discountMap = {
      '25%': 100,
      '27%': 200,
      '29%': 350,
      '30%': 600
    };
  
    const formatCurrency = (amount) => {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: activeCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    };
  
    const icons = document.querySelectorAll('.rebuy-bundle-builder__progress-step-icon-type-percentage span');
  
    icons.forEach(span => {
      const text = span.textContent.trim();
      if (!discountMap[text]) return;
  
      const baseUSD = discountMap[text];
      const converted = baseUSD * currencyRate;
  
      span.textContent = `${formatCurrency(converted)} off`;
    });
  }
  

  formatPrice(amount) {
    const activeCurrency = Shopify?.currency?.active || 'USD';
    const currencyRate = parseFloat(Shopify?.currency?.rate) || 1;
    const currencySymbol = Shopify?.currency?.symbol || '$';
  
    return `${currencySymbol}${amount}`;
  }

  setLoading(state) {
    const wrapper = document.querySelector('.rebuy__builder');
    if (!wrapper) return;
    wrapper.classList.toggle('is-loading', state);
    if (!state) wrapper.classList.add('is-loaded');
    else wrapper.classList.remove('is-loaded');
  }

  hideLoader() {
    const wrapper = document.querySelector('.rebuy__builder');
    if (wrapper) wrapper.style.opacity = '1';
  }

  // --------- CURRENCY ---------
  convertCurrencyValues() {
    if (!window.Shopify || !Shopify.currency) return;

    const active = Shopify.currency.active || 'USD';
    const rate   = Number(Shopify.currency.rate) || 1;

    const fmt = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: active,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });

    const parseAmount = (str) => {
      let s = (str || '').replace(/[^\d.,-]/g, '');
      if (s.includes(',') && s.includes('.')) {
        if (s.lastIndexOf('.') > s.lastIndexOf(',')) s = s.replace(/,/g, '');
        else s = s.replace(/\./g, '').replace(',', '.');
      } else if (s.includes(',')) {
        s = s.replace(',', '.');
      }
      const n = parseFloat(s);
      return Number.isNaN(n) ? null : n;
    };

    const replaceMoneyInText = (el) => {
      if (!el) return;
      if (!el.dataset.origText) el.dataset.origText = el.textContent;
      const src = el.dataset.origText;

      const moneyRe = /(?:\p{Sc}|\b(?:USD|EUR|GBP)\b)?\s*-?\d[\d.,]*/gu;

      const out = src.replace(moneyRe, (m) => {
        const amt = parseAmount(m);
        return amt == null ? m : fmt.format(amt * rate);
      });

      if (el.textContent !== out) el.textContent = out;
    };

    document
      .querySelectorAll('.rebuy-bundle-builder__product-holder-detail-description')
      .forEach(replaceMoneyInText);

    document
      .querySelectorAll('.rebuy-bundle-builder__progress-step-icon-type-fixed')
      .forEach((el) => {
        replaceMoneyInText(el);
        el.classList.add('js-currency-fixed');
      });

    document
      .querySelectorAll('.rebuy-bundle-builder__progress-step-label')
      .forEach(replaceMoneyInText);
  }

  observeCurrencyConversions() {
    const root = document.querySelector('.rebuy-bundle-builder') || document.body;
    if (this._currencyMo) return;

    const run = () => {
      if (this._currencyRunning) return;
      this._currencyRunning = true;
      if (this._currencyMo) this._currencyMo.disconnect();
      try {
        this.convertCurrencyValues();
      } finally {
        this._currencyRunning = false;
        this._currencyMo.observe(root, { childList: true, subtree: true });
      }
    };

    this._currencyMo = new MutationObserver((mutations) => {
      if (mutations.some(m => m.addedNodes.length || m.removedNodes.length)) run();
    });

    run(); // initial pass
  }

  // --------- WIDGET / DOM READY ---------
  async waitForWidgetReady() {
    await new Promise((r) => {
      const tick = () => {
        this.refreshWidgetState();
        if (this.widget) return r();
        requestAnimationFrame(tick);
      };
      tick();
    });

    const ids = (this.steps.flatMap(s => (Array.isArray(s.products) ? s.products.map(p => p.id) : []))) || [];
    if (ids.length) {
      try { await this.waitForProductCards(ids); } catch { /* ok */ }
    }
  }

  waitForProductCards(productIds, { timeout = 6000 } = {}) {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const hasCards = () => productIds.some(id => document.querySelector(`.rebuy-product-block.product-id-${id}`));
      if (hasCards()) return resolve();

      const obs = new MutationObserver(() => {
        if (hasCards()) { obs.disconnect(); resolve(); }
        else if (Date.now() - start > timeout) { obs.disconnect(); reject(new Error('Timed out waiting for product cards')); }
      });

      obs.observe(document.body, { childList: true, subtree: true });

      const t = setInterval(() => {
        if (hasCards()) { clearInterval(t); obs.disconnect(); resolve(); }
        if (Date.now() - start > timeout) { clearInterval(t); obs.disconnect(); reject(new Error('Timed out waiting for product cards')); }
      }, 100);
    });
  }

  // --------- INIT ---------
  async init() {
    this.applyGlobalSettings();
    this.replaceDiscountPercentWithText();

    await this.waitForWidgetReady();

    this.setLoading(true);
    await this.data();
    this.hideDuplicateCompareAt();
    this.setLoading(false);
    this.observeCurrencyConversions();

    this.hideLoader();
    this.installAddAndCheckoutWhenCTAIsReady();
    this.initCompareAtObserver();

    this.tabs.forEach(tab => {
      tab.addEventListener('click', async () => {
        this.setLoading(true);
        await this.data();
        this.setLoading(false);
        this.observeCurrencyConversions();
        this.hideLoader();
        this.hideDuplicateCompareAt();
      });
    });
  }

  initCompareAtObserver() {
    const container = document.querySelector('.rebuy-bundle-builder__product-holder-data')?.parentNode;
    const root = document.querySelector('.rebuy-bundle-builder');
    if (!root) return;

    const run = () => this.hideDuplicateCompareAt();
    run();

    const mo = new MutationObserver(() => run());
    mo.observe(root, { childList: true, subtree: true });
  }
  

  installAddAndCheckout() {
    if (this._installedAddCheckout) return;
    const container = document.querySelector('.rebuy-bundle-builder__cta-container');
    const rebuyCTA = container?.querySelector('.rebuy-button');
    if (!container || !rebuyCTA) return;

    let customBtn = container.querySelector('.rebuy-button--add-and-checkout');
    if (!customBtn) {
      customBtn = document.createElement('button');
      customBtn.type = 'button';
      customBtn.className = 'rebuy-button rebuy-button--add-and-checkout';
      customBtn.textContent = 'Checkout';
      rebuyCTA.insertAdjacentElement('afterend', customBtn);
    }

    // keep Rebuy CTA visible; do NOT hide or remove
    // (Copy the label so both buttons look consistent)
    const sync = () => { customBtn.innerHTML = rebuyCTA.innerHTML; };
    sync();
    new MutationObserver(sync).observe(rebuyCTA, { childList: true, subtree: true, characterData: true });

    customBtn.addEventListener('click', () => {
      // Let Rebuy do the heavy lifting
      rebuyCTA.click();

      let redirected = false;
      const goCheckout = () => {
        if (redirected) return;
        redirected = true;
        window.location.href = '/checkout';
      };

      // Handy helper to listen once
      const once = (name, fn) => {
        const handler = (e) => { try { fn(e); } finally { document.removeEventListener(name, handler); } };
        document.addEventListener(name, handler);
        return () => document.removeEventListener(name, handler);
      };

      // Best case: bundle success event (available on Bundle Builder pages)
      const offSuccess = once('rebuy.addBundleToCartSuccess', (e) => {
        const id = e?.detail?.id ?? e?.detail?.widget_id ?? e?.detail?.widgetId;
        if (!id || String(id) === String(this.widgetId)) goCheckout();
      });

      // Fallbacks: cart events
      const offAdd    = once('rebuy:cart.add',    goCheckout);
      const offChange = once('rebuy:cart.change', goCheckout);

      // Error: show a message and don’t redirect
      const offError  = once('rebuy.addBundleToCartError', (e) => {
        offSuccess(); offAdd(); offChange();
        console.error('Rebuy bundle add failed:', e?.detail);
        alert('Sorry—something went wrong adding your bundle. Please try again.');
      });

      // Safety timeout so we never hang
      setTimeout(() => { offSuccess(); offAdd(); offChange(); offError(); }, 10000);
    });

    this._installedAddCheckout = true;
  }

  installAddAndCheckoutWhenCTAIsReady() {
    // if CTA exists now – install immediately
    const container = document.querySelector('.rebuy-bundle-builder__cta-container');
    if (container?.querySelector('.rebuy-button')) {
      this.installAddAndCheckout();
      return;
    }
    // else observe until CTA appears
    const mo = new MutationObserver(() => {
      if (document.querySelector('.rebuy-bundle-builder__cta-container .rebuy-button')) {
        mo.disconnect();
        this.installAddAndCheckout();
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  // --------- DATA / RENDER ENRICHMENTS ---------
  async data() {
    this.refreshWidgetState();

    const ids = [];
    this.steps.forEach(step => {
      if (Array.isArray(step.products)) step.products.forEach(p => ids.push(p.id));
    });

    if (ids.length) {
      try { await this.waitForProductCards(ids); } catch { /* ok */ }
    }

    const fetches = [];
    this.steps.forEach(step => {
      if (Array.isArray(step.products)) {
        step.products.forEach(product => {
          const productCards = this.getProductCards(product.id);
          const fetchPromise = this.fetchProductMetafields(product.id)
            .then(fullProduct => {
              productCards.forEach(card => {
                const badge = fullProduct?.metafields?.theme?.badge;
                if (badge) this.renderBadge(fullProduct, card);
                this.renderProtectionAndTitle(fullProduct, card);
                this.replaceProductImageFromMetafield(fullProduct, card);
              });
            })
            .catch(() => {});
          fetches.push(fetchPromise);
        });
      }
    });

    await Promise.allSettled(fetches);

    this.hideDuplicateCompareAt();
  }

  async fetchProductMetafields(productId) {
    const endpoint = `/api/${this.apiVersion}/graphql.json`;
    const variables = { id: `gid://shopify/Product/${productId}` };

    const query = `
      query GetProductMetafields($id: ID!) {
        product(id: $id) {
          id
          metafields(identifiers: [
            { namespace: "theme", key: "badge" },
            { namespace: "custom", key: "product_card_data" },
            { namespace: "custom", key: "image_for_product_card_bundle_page" }
          ]) {
            namespace
            key
            value
            type
            reference {
              ... on MediaImage {
                image { url altText }
              }
              ... on Metaobject {
                id
                type
                fields { key value type }
              }
            }
          }
        }
      }
    `;

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontToken
      },
      body: JSON.stringify({ query, variables })
    });

    const data = await res.json();
    if (!data?.data?.product) return null;

    let metafields = {};
    let metaobjectData = null;

    (data.data.product.metafields || [])
      .filter(Boolean)
      .forEach(field => {
        if (!metafields[field.namespace]) metafields[field.namespace] = {};

        if (
          field.namespace === 'custom' &&
          field.key === 'image_for_product_card_bundle_page' &&
          field.reference?.image?.url
        ) {
          metafields[field.namespace][field.key] = field.reference.image.url;
        } else {
          metafields[field.namespace][field.key] = field.value;
        }

        if (field.namespace === 'custom' && field.key === 'product_card_data' && field.reference) {
          metaobjectData = field.reference;
        }
      });

    return {
      ...data.data.product,
      metafields,
      product_card_metaobject: metaobjectData
    };
  }

  hideDuplicateCompareAt() {
    document.querySelectorAll('.rebuy-bundle-builder__product-holder-data').forEach(holder => {
      const saleEl = holder.querySelector('.rebuy-money.sale span[tabindex]');
      const compareWrap = holder.querySelector('.rebuy-money.compare-at');
      const compareEl = compareWrap?.querySelector('span:not([class])');
  
      if (saleEl && compareEl) {
        const saleText = saleEl.textContent.replace(/[^\d.,]/g, '').trim();
        const compareText = compareEl.textContent.replace(/[^\d.,]/g, '').trim();
  
        if (saleText && compareText && saleText === compareText) {
          compareWrap.style.display = 'none';
        } else {
          compareWrap.style.display = '';
        }
      }
    });
  }
  

  applyGlobalSettings() {
    const icon = document.querySelector('.rebuy-bundle-builder__progress-step-icon-type-none');
    const headerEl = document.querySelector('.rebuy-bundle-builder__main-header');
    const stepsContainer = document.querySelector('.rebuy-bundle-builder__steps-container');

    if (headerEl && stepsContainer) {
      stepsContainer.insertBefore(headerEl, stepsContainer.firstChild);
    }

    if (icon) icon.textContent = 'Free Shipping';

    document.querySelectorAll('.rebuy-bundle-builder__progress-step-icon-type-fixed').forEach(el => {
      if (!el.textContent.trim().endsWith('Off')) {
        el.textContent = el.textContent.trim() + ' Off';
      }
    });

    const starsBlock = document.querySelector('.bundle__stars');
    const actionBlock = document.querySelector('.rebuy-bundle-builder__group-container-action');
    if (starsBlock && actionBlock && !actionBlock.contains(starsBlock)) {
      actionBlock.appendChild(starsBlock);
    }

    const installmentsBlock = document.querySelector('.bundle__installments');
    if (installmentsBlock && actionBlock && !actionBlock.contains(installmentsBlock)) {
      actionBlock.prepend(installmentsBlock);
    }

    const helpSectionBlock = document.querySelector('.bundle__section');
    const rebuyBlock = document.querySelector('.rebuy-bundle-builder__group-container');
    if (helpSectionBlock && rebuyBlock && !rebuyBlock.contains(helpSectionBlock)) {
      rebuyBlock.appendChild(helpSectionBlock);
    }

    const helpSectionBlockMobile = document.querySelector('.bundle__section--mobile');
    if (helpSectionBlockMobile && headerEl && !headerEl.contains(helpSectionBlockMobile)) {
      headerEl.appendChild(helpSectionBlockMobile);
    }

    const textSaving = document.querySelector('.text__saving');
    const textSavingPosition = document.querySelector('.rebuy-bundle-builder__group-container-header--left');
    if (textSaving && textSavingPosition && !textSavingPosition.contains(textSaving)) {
      textSavingPosition.appendChild(textSaving);
    }
  }

  getProductCards(productId) {
    const productIdClass = `product-id-${productId}`;
    return document.querySelectorAll(`.rebuy-product-block.${productIdClass}`);
  }

  replaceProductImageFromMetafield(product, productCard) {
    const imageUrl = product?.metafields?.custom?.image_for_product_card_bundle_page;
    if (!imageUrl) return;

    const imageLink = productCard.querySelector('.rebuy-product-image');
    const imageEl = imageLink?.querySelector('img');
    if (imageEl) imageEl.src = imageUrl;
  }

  renderBadge(product, productCard) {
    const badge = product?.metafields?.theme?.badge;
    if (!badge) return;

    const mediaBlock = productCard.querySelector('.rebuy-product-media');
    if (!mediaBlock || mediaBlock.querySelector('.badge-box')) return;

    const badgeBox = document.createElement('div');
    badgeBox.className = 'badge-box';
    badgeBox.textContent = badge;
    mediaBlock.appendChild(badgeBox);
  }

  waitForIntelligemsPrice(productId, maxAttempts = 20, delay = 200) {
    return new Promise((resolve, reject) => {
      let attempts = 0;
  
      const check = () => {
        attempts++;
        const price = window?.igData?.price?.getMinPriceByProductId?.(productId);
        if (price && price !== 0) {
          return resolve(price);
        }
  
        if (attempts >= maxAttempts) {
          return reject('Timed out waiting for Intelligems price');
        }
  
        setTimeout(check, delay);
      };
  
      check();
    });
  }

  renderProtectionAndTitle(product, productCard) {
    const productTitle = productCard.querySelector('.rebuy-product-title');
    const productPrice = productCard.querySelector('.rebuy-product-price');
  
    const metaobject = product?.product_card_metaobject?.fields || [];
    const getFieldValue = (key) => metaobject.find(field => field.key === key)?.value || '';
  
    const titleMetaobject = getFieldValue('product_title');
    const protectionMetaobject = getFieldValue('protection_radius');
    const excerptMetaobject = getFieldValue('product_excerpt');
  
    if (titleMetaobject && productTitle) {
      productTitle.textContent = `${titleMetaobject}`;
    }
  
    if (productTitle && !productTitle.querySelector('.rebuy-product-title-protection') && protectionMetaobject) {
      const protectionBox = document.createElement('div');
      protectionBox.className = 'rebuy-product-title-protection';
      protectionBox.textContent = `${protectionMetaobject}`;
      productTitle.appendChild(protectionBox);
    }
  
    if (productPrice && !productPrice.querySelector('.rebuy-product-title-excerpt') && excerptMetaobject) {
      const excerptBox = document.createElement('div');
      excerptBox.className = 'rebuy-product-title-excerpt';
      excerptBox.textContent = `${excerptMetaobject}`;
      productPrice.prepend(excerptBox);
    }
  
    // --------- 💰 Intelligems API: Заміна ціни по Product ID ---------
    const match = productCard?.className?.match(/product-id-(\d+)/);
    const productId = match?.[1];

    if (productId && window.igData?.price?.getMinPriceByProductId) {
      this.waitForIntelligemsPrice(productId)
        .then((price) => {
          if (!productPrice) return;

          const formattedPrice = this.formatPrice(price);
          const priceEl = productPrice.querySelector('.rebuy-money.sale span[tabindex]');
          if (priceEl) {
            priceEl.textContent = formattedPrice;
          }
        })
        .catch((err) => {
          console.warn(`⛔️ Intelligems price not found for product ${productId}:`, err);
        });
    }
  }
}

// ---------- BOOT ----------
document.addEventListener('rebuy.ready', () => {
  const wrapper = document.querySelector('.rebuy-bundle-builder');
  const idWidget = document.querySelector('.rebuy__builder')?.dataset?.id;
  if (wrapper && idWidget) {
    const renderer = new RebuyProductRenderer(idWidget);
    setTimeout(() => renderer.init(), 1000);
  }
});